import { useEffect, useState, useCallback } from 'react';
import type { CardFlightData } from './ProfileReveal';

export type CollapsePhase = 0 | 1 | 2 | 3;

interface CollapseState {
  phase: CollapsePhase;
  cardFlights: CardFlightData | null;
  particleTarget: { xPct: number; yPct: number } | null;
}

interface UseCollapseTransitionOptions {
  profileTargetRef: React.RefObject<HTMLDivElement | null>;
  onTransitionComplete: () => void;
}

export function useCollapseTransition({ profileTargetRef, onTransitionComplete }: UseCollapseTransitionOptions) {
  const [state, setState] = useState<CollapseState>({
    phase: 0,
    cardFlights: null,
    particleTarget: null,
  });

  const startCollapse = useCallback((flights: CardFlightData) => {
    setState({
      phase: 1,
      cardFlights: flights,
      particleTarget: null,
    });
  }, []);

  // Phase 1 → 2: measure target position, set particle vacuum target
  useEffect(() => {
    if (state.phase !== 1) return;
    const raf = requestAnimationFrame(() => {
      const targetEl = profileTargetRef.current;
      if (!targetEl) {
        onTransitionComplete();
        setState({ phase: 0, cardFlights: null, particleTarget: null });
        return;
      }
      const targetRect = targetEl.getBoundingClientRect();

      setState(prev => ({
        phase: 2,
        cardFlights: prev.cardFlights ? { ...prev.cardFlights, targetRect } : null,
        particleTarget: {
          xPct: (targetRect.left + targetRect.width / 2) / window.innerWidth,
          yPct: (targetRect.top + targetRect.height / 2) / window.innerHeight,
        },
      }));
    });
    return () => cancelAnimationFrame(raf);
  }, [state.phase, profileTargetRef, onTransitionComplete]);

  // Phase 2 → 3 → complete: wait for animation, then finalize
  useEffect(() => {
    if (state.phase !== 2) return;
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, phase: 3, particleTarget: null }));
      setTimeout(() => {
        onTransitionComplete();
        setState({ phase: 0, cardFlights: null, particleTarget: null });
      }, 200);
    }, 1200);
    return () => clearTimeout(timer);
  }, [state.phase, onTransitionComplete]);

  // Compute flight styles for a card during phase 2
  const getCardFlightStyle = useCallback((index: number): React.CSSProperties | undefined => {
    if (state.phase < 2 || !state.cardFlights) return undefined;
    const fromRect = state.cardFlights.flights[index];
    const toRect = state.cardFlights.targetRect;
    if (!fromRect || !toRect || toRect.width === 0) return undefined;

    const toCx = toRect.left + toRect.width / 2;
    const toCy = toRect.top + toRect.height / 2;
    const fromCx = fromRect.left + fromRect.width / 2;
    const fromCy = fromRect.top + fromRect.height / 2;
    const dx = toCx - fromCx;
    const dy = toCy - fromCy;

    return {
      transform: `translate(${dx}px, ${dy}px) scale(0.08)`,
      opacity: 0,
      borderRadius: 40,
      transition: `all 0.85s cubic-bezier(0.65, 0, 0.15, 1) ${index * 55}ms`,
      zIndex: 20 - index,
    };
  }, [state.phase, state.cardFlights]);

  return {
    collapsePhase: state.phase,
    particleTarget: state.particleTarget,
    startCollapse,
    getCardFlightStyle,
  };
}
